import { useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import ReCAPTCHA from "react-google-recaptcha";
import { signInWithEmailAndPassword, sendPasswordResetEmail } from "firebase/auth";
import { auth, FIREBASE_READY } from "../firebase";
import flagImg from "../assets/korean-flag.png";
import logoImg from "../assets/papualng-logo.png";

/* reCAPTCHA v2 *site* key (public). The secret key must never be shipped in
   client code — it is only for server-side verification. Widget is skipped
   if no site key is configured, so local/dev builds still work. */
const RECAPTCHA_SITE_KEY = process.env.REACT_APP_RECAPTCHA_SITE_KEY;
const RECAPTCHA_READY    = !!RECAPTCHA_SITE_KEY;

export default function AdminLogin() {
  const navigate = useNavigate();
  const recaptchaRef = useRef(null);
  const [captchaToken, setCaptchaToken] = useState(null);

  const [email,        setEmail]        = useState("");
  const [password,     setPassword]     = useState("");
  const [err,          setErr]          = useState("");
  const [loading,      setLoading]      = useState(false);
  const [forgotMode,   setForgotMode]   = useState(false);
  const [resetEmail,   setResetEmail]   = useState("");
  const [resetSent,    setResetSent]    = useState(false);
  const [resetErr,     setResetErr]     = useState("");
  const [resetLoading, setResetLoading] = useState(false);

  const login = async () => {
    setErr("");
    if (!FIREBASE_READY) { setErr("Firebase isn't configured yet. See .env.example."); return; }
    if (RECAPTCHA_READY && !captchaToken) { setErr("Please confirm you're not a robot."); return; }
    setLoading(true);
    try {
      await signInWithEmailAndPassword(auth, email, password);
      navigate("/admin");
    } catch {
      setErr("Invalid email or password.");
      if (RECAPTCHA_READY) { recaptchaRef.current?.reset(); setCaptchaToken(null); }
    }
    setLoading(false);
  };

  const sendReset = async () => {
    setResetErr("");
    if (!resetEmail) { setResetErr("Please enter your email address."); return; }
    setResetLoading(true);
    try {
      await sendPasswordResetEmail(auth, resetEmail);
      setResetSent(true);
    } catch (e) {
      if (e.code === "auth/user-not-found" || e.code === "auth/invalid-credential") {
        setResetErr("No account found with that email address.");
      } else if (e.code === "auth/invalid-email") {
        setResetErr("Please enter a valid email address.");
      } else if (e.code === "auth/too-many-requests") {
        setResetErr("Too many requests. Please wait a few minutes and try again.");
      } else {
        setResetErr(`Could not send reset email (${e.code || "unknown"}). Please try again.`);
      }
    }
    setResetLoading(false);
  };

  const onKey    = e => e.key === "Enter" && login();
  const onKeyRst = e => e.key === "Enter" && sendReset();

  const Logo = () => (
    <div className="login-logo">
      <img src={flagImg} alt="Korea" className="lg-flag"/>
      <img src={logoImg} alt="Papua LNG Project" className="lg-mark"/>
    </div>
  );

  if (forgotMode) {
    return (
      <div className="login-wrap">
        <div className="login-box">
          <Logo/>
          <div className="login-title">Reset Password</div>
          <div className="login-sub">Enter your email to receive a reset link</div>

          {resetSent ? (
            <div style={{ background: "#e8f5e9", border: "1.5px solid #a5d6a7", borderRadius: 12, padding: "1.25rem", textAlign: "center", margin: "1rem 0" }}>
              <div style={{ fontSize: "1.75rem", marginBottom: ".4rem" }}>📧</div>
              <div style={{ fontWeight: 700, color: "#2e7d32", marginBottom: ".3rem" }}>Reset Email Sent</div>
              <div style={{ fontSize: ".83rem", color: "#4e6058" }}>
                Check <strong>{resetEmail}</strong> for a password reset link.
              </div>
            </div>
          ) : (
            <>
              {resetErr && <div className="login-err">{resetErr}</div>}
              <div className="field">
                <label>Email Address</label>
                <input type="email" value={resetEmail}
                  onChange={e => setResetEmail(e.target.value)}
                  placeholder="your@email.com" onKeyDown={onKeyRst} autoFocus/>
              </div>
              <button className="btn-g" style={{ width: "100%", padding: ".7rem" }} onClick={sendReset} disabled={resetLoading}>
                {resetLoading ? "Sending…" : "Send Reset Email →"}
              </button>
            </>
          )}
          <button className="btn-gray" style={{ width: "100%", marginTop: ".65rem" }}
            onClick={() => { setForgotMode(false); setResetSent(false); setResetEmail(""); setResetErr(""); }}>
            ← Back to Sign In
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="login-wrap">
      <div className="login-box">
        <Logo/>
        <div className="login-title">Korea Papua LNG Admin</div>
        <div className="login-sub">Submissions Manager</div>
        {err && <div className="login-err">{err}</div>}
        <div className="field">
          <label>Email Address</label>
          <input type="email" value={email}
            onChange={e => setEmail(e.target.value)}
            placeholder="admin@example.com" onKeyDown={onKey}/>
        </div>
        <div className="field">
          <label>Password</label>
          <input type="password" value={password}
            onChange={e => setPassword(e.target.value)}
            placeholder="Enter password" onKeyDown={onKey}/>
        </div>
        {RECAPTCHA_READY && (
          <div className="field login-captcha">
            <ReCAPTCHA ref={recaptchaRef} sitekey={RECAPTCHA_SITE_KEY}
              onChange={setCaptchaToken} onExpired={() => setCaptchaToken(null)}/>
          </div>
        )}
        <button className="btn-g" style={{ width: "100%", padding: ".7rem" }} onClick={login} disabled={loading}>
          {loading ? "Signing in…" : "Sign In →"}
        </button>
        <button className="btn-gray"
          style={{ width: "100%", marginTop: ".5rem", background: "none", border: "none", color: "var(--g)", fontSize: ".85rem", cursor: "pointer", padding: ".4rem" }}
          onClick={() => { setForgotMode(true); setResetEmail(email); }}>
          Forgot password?
        </button>
        <button className="btn-gray" style={{ width: "100%", marginTop: ".65rem" }} onClick={() => navigate(-1)}>
          ← Back to Website
        </button>
      </div>
    </div>
  );
}
